import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';

import useErrorManager from "../../../hooks/useErrorManager";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";

import GenericDetailsTable from "../../Layout/Table/GenericDetailsTable";
import GenericDatePicker from "../../Layout/GenericDatePicker";
import CtModeSelect from "../../Layout/CTModeSelect";
import DmReciveModeSelect from "../../Layout/DmReciveModeSelect";
import GenericTimePicker from "../../Layout/GenericTimePicker";

import { ExpandMore, Edit, Delete } from '@mui/icons-material';

import { Button, Box, Typography, CircularProgress, Grid, Alert, Select, MenuItem, IconButton, RadioGroup, Radio, FormControlLabel } from "@mui/material";

import { useNotification } from '../../../contexts/NotificationContext';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext'; // Import breadcrumb context

const DEFAULT_READOUT_DETAILS = {
    ctMode: "C1T1",
    taskPlanTimeIdList: [],
    monthlyRecurrenceType: "DayInMonth"
};

const WEEKDAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

const MetersReadout = forwardRef(({ gwDetails, setIsValid, readoutType }, ref) => {
    const { gatewayId } = useParams();

    const { setError, clearError, getError } = useErrorManager();
    const { showNotification } = useNotification();
    
    const navigate = useNavigate();
    const axiosInstance = useAxiosPrivate();

    const [metersRODetails, setMetersRODetails] = useState(DEFAULT_READOUT_DETAILS);

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchReadoutDetails();
    }, [gatewayId, readoutType]);

    const fetchReadoutDetails = async () => {
        try {
            setLoading(true);
            const endpoint = readoutType === "Maintenance" 
                ? `/meters/readout/${gatewayId}/maintenance` 
                : `/meters/readout/${gatewayId}/recurring`;
            const response = await axiosInstance.get(endpoint);
            response.data.monthlyRecurrenceType = "DayInMonth";
            if (
                response.data.ctMode === null ||
                response.data.ctMode === undefined ||
                response.data.ctMode === "undefined"
            ) {
                response.data.ctMode = "C1T1";
            }
            if (
                response.data.dmReceiveMode === null ||
                response.data.dmReceiveMode === undefined ||
                response.data.dmReceiveMode === "SystemDefault"
            ) {
                response.data.dmReceiveMode = "WbAmr";
            }
            setMetersRODetails(response.data || DEFAULT_READOUT_DETAILS); // Ensure we always have a valid object
            validateForm(response.data || DEFAULT_READOUT_DETAILS);
            clearError('fetchReadoutDetails');
        } catch (error) {
            setError('fetchReadoutDetails', error.message);
        } finally {
            setLoading(false);
        }
    };

    
    const handleRODetailsChange = (field, value) => {
        setMetersRODetails(prev => ({
            ...prev,
            [field]: value
        }));
        validateForm({ ...metersRODetails, [field]: value });
    };

    const handleDateTimeChange = (newValue, index, type) => {
        const currentDate = new Date(metersRODetails.taskPlanTimeIdList[index].time);
        //QUI DEVO INSERIRE CHE, SE VIENE AGGIUNTA UNA NUOVA TASK O VIENE MODIFICATO IL DAY DI UNA TASK, ALLORA VIENE IMPOSTATO ANCHE recurrenceDayInMonth = DAY
        let updatedDate;
        if (type === 'date') {
          // Use the new date values and preserve the current time
          updatedDate = new Date(
            newValue.year(),
            newValue.month(),
            newValue.date(),
            currentDate.getHours(),
            currentDate.getMinutes(),
            currentDate.getSeconds()
          );
        } else if (type === 'time') {
          // Use the new time values and preserve the current date
          updatedDate = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate(),
            newValue.hour(),
            newValue.minute(),
            newValue.second()
          );
        }

        const updatedDates = [...metersRODetails.taskPlanTimeIdList];
        updatedDates[index] = { ...updatedDates[index], time: updatedDate, recurrenceDayInMonth: getRecurrenceDayByDate(updatedDate) };
        handleRODetailsChange("taskPlanTimeIdList", updatedDates);
    };


    const getRecurrenceDayByDate = (date) => {
        return date.getDate();
    };


    const addRecurringDate = () => {
        if (metersRODetails.taskPlanTimeIdList.length < (readoutType === "Maintenance" ? 1 : Math.floor(gwDetails.tariffInfo.dmRecurringReadoutsPerYear/12))) {
            const now = new Date();
            handleRODetailsChange("taskPlanTimeIdList", [...metersRODetails.taskPlanTimeIdList, { time: now, recurrenceDayInMonth: getRecurrenceDayByDate(now)}]);
        }
    };

    const removeRecurringDate = (index) => {
        handleRODetailsChange("taskPlanTimeIdList", metersRODetails.taskPlanTimeIdList.filter((_, i) => i !== index));
    };

    const validateForm = (data) => {
        const hasValidTimes = data.taskPlanTimeIdList.every(entry => entry.time && new Date(entry.time) > new Date());
        const isValid = data.ctMode !== "" && data.monthlyRecurrenceType !== "" && hasValidTimes;
        setIsValid(isValid); // Send validation result to parent
    };

    useImperativeHandle(ref, () => ({
        saveData: async () => {
            try {
                const payload = {
                    type: readoutType,
                    ctMode: metersRODetails.ctMode,
                    dmReceiveMode: metersRODetails.dmReceiveMode,
                    description: "",
                    duration: 1880, // Ensure duration is correctly passed
                    // times: metersRODetails.taskPlanTimeIdList.map(entry => entry.time).filter(Boolean), // Extract times only VECCHIO METODO
                    taskPlanTimeIdList: metersRODetails.taskPlanTimeIdList
                };
                await axiosInstance.put(`/meters/${gatewayId}/readout`, payload);
                clearError('createScan');
                showNotification('Meters Readout saved succesfully', 'success');
                navigate(`/gateways/${gatewayId}`);
            } catch (error) {
                setError('createScan', "There was an error saving meters readout. Please try again later.");
                showNotification('Error saving meters readout', 'error');
            }
        },
    }));

    const ROInfoRows = [
        { key: "ctMode", label: "Mode",
            render: (item, updateItem) => (
                <CtModeSelect 
                    value={item.ctMode} 
                    onChange={(newValue) => handleRODetailsChange("ctMode", newValue)}
                />
            ), 
        },
        { key: "dmReceiveMode", label: "Message Receiving Mode",
            render: (item, updateItem) => (
                <DmReciveModeSelect 
                    value={item.dmReceiveMode} 
                    onChange={(newValue) => handleRODetailsChange("dmReceiveMode", newValue)}
                />
            ), 
        },
        { key: "monthlyRecurrenceType", label: "Recurrence Type", render: (item) => item.monthlyRecurrenceType === "DayInMonth" ? "Day in Month" : "Weekday" }
    ];
    const tariffInfoRows = [
        { key: "tariff", label: "Tariff", render: (item) => item.tariffInfo.asn  },
        readoutType === "Maintenance"
        ? {
            key: "maintenanceROperYear",
            label: "Maintenance Readouts per Year",
            render: item =>
            `${item.tariffInfo.dmMaintenanceReadoutsPerYear} (${item.usedDMMaintenanceReadoutsPerYear || 0} used)`
        }
        : {
            key: "recurringROperYear",
            label: "Recurring Readouts per Year",
            render: item => `${item.tariffInfo.dmRecurringReadoutsPerYear}`
        }
    ];

    if (loading) return <CircularProgress />;
    if (getError('fetchReadoutDetails')) return <Typography variant="h7">Error fetching recurring readout details: {getError('fetchReadoutDetails')}</Typography>;

    return (
        <Box sx={{ marginY: 2 }}>
            <GenericDetailsTable
                detailsValue={metersRODetails}
                detailsList={ROInfoRows} />
            <GenericDetailsTable
                detailsValue={gwDetails}
                detailsList={tariffInfoRows} />

            <Grid container spacing={2} sx={{ marginY: 1 }}>
                {metersRODetails.taskPlanTimeIdList.map((entry, index) => (
                    <Grid item xs={12} key={index}>
                        <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                            <GenericDatePicker initialDate={entry.time} onDateChange={(newDate) => handleDateTimeChange(newDate, index, 'date')} />
                            <GenericTimePicker initialTime={entry.time} onTimeChange={(newTime) => handleDateTimeChange(newTime, index, 'time')} />
                        
                            <IconButton onClick={() => removeRecurringDate(index)}>
                               <Delete />
                            </IconButton>
                            
                            {new Date(entry.time) < new Date() && (
                                <Alert 
                                    severity="error" 
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        borderRadius: 2, // Add rounded corners
                                    }}
                                >
                                    Date must not be in the past!
                                </Alert>
                            )}

                            {gwDetails.nextConnection &&
                                new Date(entry.time) < new Date(gwDetails.nextConnection) && (
                                    <Alert 
                                        severity="info" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            borderRadius: 2, // Add rounded corners
                                        }}
                                    >
                                        Selected time is before the next device reconnect ({new Intl.DateTimeFormat('it-EU', { 
                                            day: '2-digit', 
                                            month: '2-digit', 
                                            year: 'numeric', 
                                            hour: '2-digit', 
                                            minute: '2-digit',
                                            hour12: false 
                                        }).format(new Date(gwDetails.nextConnection))}). 
                                        Please choose a later time or trigger Reconnect manually on device.
                                    </Alert>
                                )
                            }
                        
                        </Box>
                    </Grid>
                ))}
                {metersRODetails.taskPlanTimeIdList.length < (readoutType === "Maintenance" ? 1 :  Math.floor(gwDetails.tariffInfo.dmRecurringReadoutsPerYear/12)) && (
                    <Grid item xs={12}>
                        <Button variant="contained" color="primary" onClick={addRecurringDate}>ADD DATE</Button>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
});

export default MetersReadout;
